import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';

import { useFilterContext } from 'src/components/app/context/FilterContext';
import Accordion, {
  AccordionItem,
  AccordionItemHeader,
  AccordionItemPanel,
} from 'src/components/common/Accordion';
import OrgTree from 'src/components/common/OrgTree';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { translateFilters } from 'src/components/planAction/PlanActionStatisticsPage/helpers';

const Sidebar = ({ extendedFilters }) => {
  const { t } = useTranslation('planAction');
  const { orgTreeState, planActionStatus } = usePlanActionContext();
  const { filterGroups, setFilter } = useFilterContext();
  const [filterItems, setFilterItems] = useState({});

  const corporateDemographicsFilter = (corporateFilter) =>
    planActionStatus && planActionStatus.value
      ? corporateFilter.map((value) => ({
          label: value,
          value: value,
        }))
      : [];
  const [parentFilters, setParentFilters] = useState({
    corporate_regions: corporateDemographicsFilter(
      planActionStatus.value?.corporate_regions,
    ),
    corporate_regions_overall: corporateDemographicsFilter(
      planActionStatus.value?.corporate_regions_overall,
    ),
    country: corporateDemographicsFilter(planActionStatus.value?.country),

    divisional_filter: corporateDemographicsFilter(
      planActionStatus.value?.divisional_filters,
    ),
    function: corporateDemographicsFilter(planActionStatus.value?.functions),
    level: ['parent', 'child'],
    pre_coded_managers: corporateDemographicsFilter(
      planActionStatus.value?.pre_coded_managers,
    ),
    priority: [true, false],
  });

  useEffect(() => {
    setFilterItems({
      corporateFunctions: { ...parentFilters },
      ...extendedFilters,
    });
  }, [extendedFilters, parentFilters]);

  useEffect(() => {
    setParentFilters({
      ...parentFilters,

      corporate_regions: corporateDemographicsFilter(
        planActionStatus.value?.corporate_regions,
      ),
      corporate_regions_overall: corporateDemographicsFilter(
        planActionStatus.value?.corporate_regions_overall,
      ),
      country: corporateDemographicsFilter(planActionStatus.value?.country),

      divisional_filter: corporateDemographicsFilter(
        planActionStatus.value?.divisional_filters,
      ),
      function: corporateDemographicsFilter(planActionStatus.value?.functions),
      level: ['parent', 'child'],
      pre_coded_managers: corporateDemographicsFilter(
        planActionStatus.value?.pre_coded_managers,
      ),
      priority: [true, false],
    });
  }, [planActionStatus]);

  const handleFilterSelect = (key, value) => {
    const label = t(`Statistics.Filters.nestedFilters.${key}`);

    setFilter(key, [
      {
        label,
        filterExpr: { [key]: value },
      },
    ]);
  };

  return (
    <Accordion initiallyActiveItems={['plan-action-accordion-org-tree']}>
      <AccordionItem itemName="plan-action-accordion-org-tree">
        <AccordionItemHeader headline={t('Statistics.Filters.OrgTree')} />

        <AccordionItemPanel>
          <OrgTree orgTreeState={orgTreeState} orgUnitsFromPermission />
        </AccordionItemPanel>
      </AccordionItem>

      {Object.keys(filterItems).map((item) => {
        /* Don't show the filter if it doesn't have any keys */
        const filterArray = Object.keys(filterItems[item]);
        const onlyEmptyValues = filterArray
          .map((fil) => filterItems[item][fil].filter((el) => el !== ''))
          .every((val) => val.length === 0);

        return (
          !onlyEmptyValues && (
            <AccordionItem
              itemName={`plan-action-accordion-${item}`}
              key={item}
            >
              <AccordionItemHeader headline={t(`Statistics.Filters.${item}`)} />
              <AccordionItemPanel>
                <List>
                  {Object.keys(filterItems[item])
                    .filter(
                      (element) =>
                        !!filterItems[item][element].filter((val) => val !== '')
                          .length,
                    )
                    .map((element) => {
                      /* Don't show the filter if it doesn't have any selections. */
                      const nestedKeys = filterItems[item][element].filter(
                        (val) => val !== '',
                      );

                      return (
                        <AccordionItem
                          key={element}
                          itemName={`plan-action-accordion-subitem-${element}`}
                          nested
                        >
                          <ListItem>
                            <Stack direction="column">
                              <AccordionItemHeader
                                nested
                                headline={t(
                                  `Statistics.Filters.nestedFilters.${element}`,
                                )}
                              />
                              <AccordionItemPanel>
                                <List dense>
                                  {nestedKeys.map((value) => {
                                    const selectedElement = !!filterGroups[
                                      element
                                    ]?.find(
                                      (filterValue) =>
                                        filterValue.filterExpr[element] ===
                                          value ||
                                        filterValue.filterExpr[element] ===
                                          value.value,
                                    );
                                    const translatedText = translateFilters(
                                      value,
                                      t,
                                    );

                                    return (
                                      <ListItem key={value.label || value}>
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={selectedElement}
                                              />
                                            }
                                            label={
                                              value.label || translatedText
                                            }
                                            onClick={() =>
                                              handleFilterSelect(
                                                element,
                                                value.value || value,
                                              )
                                            }
                                          />
                                        </FormGroup>
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </AccordionItemPanel>
                            </Stack>
                          </ListItem>
                        </AccordionItem>
                      );
                    })}
                </List>
              </AccordionItemPanel>
            </AccordionItem>
          )
        );
      })}
    </Accordion>
  );
};

Sidebar.propTypes = {
  filters: PropTypes.shape({
    corporate_regions: PropTypes.string,
    corporate_regions_overall: PropTypes.string,
    country: PropTypes.string,
    divisional_filter: PropTypes.string,
    function: PropTypes.string,
    level: PropTypes.oneOf(['parent', 'child']),
    pre_coded_managers: PropTypes.string,
    priority: PropTypes.bool,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  extendedFilters: PropTypes.shape({}).isRequired,
};

export default Sidebar;
